import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { BsSend } from 'react-icons/bs';
import Message from './Message';
import DebugOverlay, { isDebugEnabledFromEnv } from './DebugOverlay';
import { client } from '../api';
import { normalizeCurrencyCode } from '../utils/formatPrice';

/** Shown until GET /api/public/config returns tenant branding (avoid wrong-tenant flash). */
const FALLBACK_GREETING = "Hello! How can I help you today?";
const FALLBACK_HEADER_TITLE = "Chat assistant";

const resolveInitialGreeting = () => FALLBACK_GREETING;
const createVisitorId = () => {
  if (window?.crypto?.randomUUID) return window.crypto.randomUUID();
  return `visitor-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const createBotMessage = (text) => ({
  id: 1,
  type: "bot",
  text,
  timestamp: new Date(),
});

/** Reject root-only "/" so joining `/api/assets/...` never yields a host-less URL (resolves to the embed origin). */
const normalizeWidgetApiUrl = (value) => {
  const s = String(value ?? "").trim();
  if (!s || s === "/") return "";
  return s.replace(/\/+$/, "");
};

/** `apiUrl` + path from public config; legacy rows may still store a full URL — strip to `/api/assets/...` first. */
const resolvePublicAssetUrl = (raw, apiBase) => {
  if (!raw) return "";
  const base = normalizeWidgetApiUrl(apiBase);
  if (!base) return "";
  const root = base.replace(/\/+$/, "");
  let path = raw.trim();
  if (!path.startsWith("/")) {
    const marker = "/api/assets/";
    const i = path.indexOf(marker);
    path = i >= 0 ? path.slice(i) : path;
  }
  if (!path.startsWith("/")) return path;
  return `${root}${path}`;
};

const ChatWidget = ({ mode = "admin" }) => {
  const [messages, setMessages] = useState([createBotMessage(resolveInitialGreeting())]);
  const [headerTitle, setHeaderTitle] = useState(FALLBACK_HEADER_TITLE);
  const [brandName, setBrandName] = useState("Nethues");
  // Footer text ("Powered by …"). "" = hide. null = not yet loaded (hidden until config arrives).
  const [poweredByText, setPoweredByText] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#bf362e");
  const [widgetWebsiteUrl, setWidgetWebsiteUrl] = useState("");
  const [userMessageColor, setUserMessageColor] = useState("#bf362e");
  const [botMessageColor, setBotMessageColor] = useState("#d5bbb9");
  const [userMessageTextColor, setUserMessageTextColor] = useState("#ffffff");
  const [botMessageTextColor, setBotMessageTextColor] = useState("#1a1a1a");
  /** Path or URL from GET /api/public/config; joined to apiUrl for <img src> */
  const [publicAvatarRaw, setPublicAvatarRaw] = useState("");
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState("");
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState("");
  const [input, setInput] = useState('');
  const [pendingQuery, setPendingQuery] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState("");
  const [apiUrl, setApiUrl] = useState(() => normalizeWidgetApiUrl(process.env.REACT_APP_API_URL || ""));
  const [widgetKey, setWidgetKey] = useState(null);
  const [sessionStorageKey, setSessionStorageKey] = useState("chat_session_id");
  const [sessionId, setSessionId] = useState(localStorage.getItem("chat_session_id") || null);
  const [visitorId, setVisitorId] = useState(localStorage.getItem("chat_visitor_id") || null);
  const [publicConfigError, setPublicConfigError] = useState("");
  const [isProfileRequired, setIsProfileRequired] = useState(false);
  const [visitorName, setVisitorName] = useState("");
  const [visitorEmail, setVisitorEmail] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  const [quotaMessage, setQuotaMessage] = useState("");
  const [idleRatingWaitSeconds, setIdleRatingWaitSeconds] = useState(120);
  const [maxResultsDefault, setMaxResultsDefault] = useState(null);
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [showRatingPrompt, setShowRatingPrompt] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [debugEnabled] = useState(() => isDebugEnabledFromEnv());
  const messageEndRef = useRef(null);
  const inputRef = useRef(null);
  const idleTimerRef = useRef(null);
  // widget.js posts the open-chat message twice (immediate + retry) for delivery reliability,
  // so a forwarded search query can arrive twice — dedupe identical queries within a short window.
  const lastForwardedQueryRef = useRef({ q: null, at: 0 });
  // Latest connection context, read by the close handler (which runs from a
  // stable message listener and would otherwise capture stale state).
  const closeCtxRef = useRef({});

  const avatarDisplayUrl = useMemo(
    () => resolvePublicAssetUrl(publicAvatarRaw, apiUrl),
    [publicAvatarRaw, apiUrl],
  );

  // Keep the close-handler's context fresh without re-binding the listener.
  useEffect(() => {
    closeCtxRef.current = { mode, apiUrl, widgetKey, sessionId };
  }, [mode, apiUrl, widgetKey, sessionId]);

  // When the visitor closes the chat, tell the backend so it can email the
  // transcript to the admin (if the tenant enabled it). Best-effort + keepalive
  // so it survives the widget being hidden.
  const notifySessionClose = () => {
    const ctx = closeCtxRef.current || {};
    if (ctx.mode !== "public" || !ctx.apiUrl || !ctx.widgetKey || !ctx.sessionId) return;
    try {
      fetch(`${ctx.apiUrl}/api/public/sessions/${ctx.sessionId}/close`, {
        method: "POST",
        keepalive: true,
        headers: { "X-Widget-Key": ctx.widgetKey, "Content-Type": "application/json" },
        body: "{}",
      }).catch(() => {});
    } catch (e) {
      /* never let close notification disrupt the widget */
    }
  };

  // Persist the conversation to localStorage so it survives navigation to a new
  // page / tab (the widget iframe shares one origin, so its storage is shared
  // across host pages). Keyed per tenant + tied to the current session; the
  // greeting-only state is not saved, and we cap history to keep storage small.
  useEffect(() => {
    if (mode !== "public" || !sessionId) return;
    if (!messages || messages.length <= 1) return;
    const suffix = sessionStorageKey?.startsWith("chat_session_id_")
      ? sessionStorageKey.slice("chat_session_id_".length)
      : "default";
    try {
      // Drop the heavy retrieval-debug payload; keep products/categories/actions
      // so restored recommendations still render.
      const slim = messages.slice(-60).map(({ retrievalDebug, ...rest }) => rest);
      localStorage.setItem(
        `chat_messages_${suffix}`,
        JSON.stringify({ session_id: sessionId, messages: slim }),
      );
    } catch (e) {
      /* storage full / serialization error — non-fatal */
    }
  }, [messages, sessionId, sessionStorageKey, mode]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when widget opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (mode !== "public") return;
    const onMessage = (event) => {
      const data = event?.data;
      // widget.js posts the string "close-chat" when the visitor closes the panel.
      if (data === "close-chat" || (data && typeof data === "object" && data.action === "close-chat")) {
        notifySessionClose();
        return;
      }
      if (!data || typeof data !== "object") return;
      if (data.action !== "open-chat") return;
      if (data.apiUrl) setApiUrl(normalizeWidgetApiUrl(data.apiUrl));
      if (data.tenantPublicKey) setWidgetKey(data.tenantPublicKey);
      if (data.chatbotInitialText) {
        setMessages((prev) => (prev.length === 1 ? [createBotMessage(data.chatbotInitialText)] : prev));
      }
      if (data.chatbotHeaderTitle) {
        setHeaderTitle(data.chatbotHeaderTitle);
      }
      if (data.query && String(data.query).trim()) {
        // Query forwarded from a host page (e.g. storefront search box) — auto-sent once ready.
        // Ignore the duplicate that widget.js's retry post produces (same query within 3s).
        const forwarded = String(data.query).trim();
        const now = Date.now();
        const last = lastForwardedQueryRef.current;
        if (!last || last.q !== forwarded || now - last.at > 3000) {
          lastForwardedQueryRef.current = { q: forwarded, at: now };
          setPendingQuery(forwarded);
        }
      }
      const keySuffix = data.tenantPublicKey || "default";
      const sessionKey = `chat_session_id_${keySuffix}`;
      const visitorKey = `chat_visitor_id_${keySuffix}`;
      const ratedSessionKey = `chat_session_rating_submitted_${keySuffix}`;
      let existingVisitorId = localStorage.getItem(visitorKey);
      if (!existingVisitorId) {
        existingVisitorId = createVisitorId();
        localStorage.setItem(visitorKey, existingVisitorId);
      }
      setSessionStorageKey(sessionKey);
      setSessionId(localStorage.getItem(sessionKey) || null);
      setVisitorId(existingVisitorId);
      const existingSession = localStorage.getItem(sessionKey) || null;
      if (existingSession && localStorage.getItem(`${ratedSessionKey}_${existingSession}`) === "true") {
        setRatingSubmitted(true);
      } else {
        setRatingSubmitted(false);
      }
      // Restore the saved conversation so reopening the widget on another page
      // (or after a reload) shows the prior messages + product recommendations.
      // Only when this tab doesn't already have a live conversation, and the
      // saved history belongs to the restored session.
      try {
        const savedRaw = localStorage.getItem(`chat_messages_${keySuffix}`);
        if (existingSession && savedRaw) {
          const saved = JSON.parse(savedRaw);
          if (
            saved &&
            saved.session_id === existingSession &&
            Array.isArray(saved.messages) &&
            saved.messages.length > 1
          ) {
            const revived = saved.messages.map((mm) => ({
              ...mm,
              timestamp: mm.timestamp ? new Date(mm.timestamp) : new Date(),
            }));
            setMessages((prev) => (prev && prev.length > 1 ? prev : revived));
          }
        }
      } catch (e) {
        /* ignore corrupt saved history */
      }
      if (!data.tenantPublicKey) {
        setPublicConfigError("Widget is misconfigured: tenantPublicKey is required.");
      } else {
        setPublicConfigError("");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [mode]);

  useEffect(() => {
    if (mode !== "public" || !widgetKey || !visitorId || !apiUrl) return;
    const checkProfile = async () => {
      try {
        const { data } = await client.get(`${apiUrl}/api/public/visitor-profile`, {
          headers: {
            "X-Widget-Key": widgetKey,
            "X-Visitor-Id": visitorId,
          },
        });
        setIsProfileRequired(!data?.profile_exists);
      } catch (err) {
        console.error("Error checking visitor profile:", err);
      }
    };
    checkProfile();
  }, [apiUrl, mode, visitorId, widgetKey]);

  useEffect(() => {
    if (mode !== "public" || !widgetKey || !apiUrl) return;
    const loadWidgetConfig = async () => {
      try {
        const { data } = await client.get(`${apiUrl}/api/public/config`, {
          headers: { "X-Widget-Key": widgetKey },
        });
        if (data?.header_title) {
          setHeaderTitle(data.header_title);
        }
        if (data?.welcome_message) {
          setMessages((prev) => (prev.length === 1 ? [createBotMessage(data.welcome_message)] : prev));
        }
        setBrandName(data?.brand_name || "Nethues");
        // Configurable footer; backend defaults to "Powered by Company Name", "" means hide.
        setPoweredByText(typeof data?.powered_by_text === "string" ? data.powered_by_text : "Powered by Company Name");
        setPrimaryColor(data?.primary_color || "#bf362e");
        setWidgetWebsiteUrl(data?.website_url || "");
        setUserMessageColor(data?.user_message_color || "#bf362e");
        setBotMessageColor(data?.bot_message_color || "#d5bbb9");
        setUserMessageTextColor(data?.user_message_text_color || "#ffffff");
        setBotMessageTextColor(data?.bot_message_text_color || "#1a1a1a");
        setPublicAvatarRaw(data?.avatar_url || "");
        setPrivacyPolicyUrl(data?.privacy_policy_url || "");
        setRecaptchaSiteKey(data?.recaptcha_site_key || "");
        setIdleRatingWaitSeconds(Number(data?.idle_rating_wait_seconds || 120));
        if (data?.max_results_default != null) {
          setMaxResultsDefault(Number(data.max_results_default));
        }
        if (data?.currency) {
          setCurrencyCode(normalizeCurrencyCode(data.currency));
        }
      } catch (err) {
        console.error("Error loading widget config:", err);
      }
    };
    loadWidgetConfig();
  }, [apiUrl, mode, widgetKey]);

  // Load the invisible reCAPTCHA v3 script once a site key is available.
  useEffect(() => {
    if (mode !== "public" || !recaptchaSiteKey) return;
    if (document.getElementById("cb-recaptcha-v3")) return;
    const s = document.createElement("script");
    s.id = "cb-recaptcha-v3";
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(recaptchaSiteKey)}`;
    s.async = true;
    document.head.appendChild(s);
    // Hide the floating badge (allowed by Google as long as the reCAPTCHA
    // disclosure text is shown — which we render under the form).
    if (!document.getElementById("cb-recaptcha-style")) {
      const st = document.createElement("style");
      st.id = "cb-recaptcha-style";
      st.textContent = ".grecaptcha-badge{visibility:hidden!important;}";
      document.head.appendChild(st);
    }
  }, [mode, recaptchaSiteKey]);

  useEffect(() => {
    if (mode !== "public" || !widgetKey || !visitorId || !apiUrl || !sessionId) return;
    const checkExistingRating = async () => {
      try {
        const { data } = await client.get(`${apiUrl}/api/public/session-rating/${sessionId}`, {
          headers: {
            "X-Widget-Key": widgetKey,
            "X-Visitor-Id": visitorId,
          },
        });
        if (data?.submitted) {
          setRatingSubmitted(true);
          setSelectedRating(Number(data.rating || 0));
          setShowRatingPrompt(false);
        } else {
          setRatingSubmitted(false);
        }
      } catch (err) {
        console.error("Error checking session rating:", err);
      }
    };
    checkExistingRating();
  }, [apiUrl, mode, sessionId, visitorId, widgetKey]);

  useEffect(() => () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
  }, []);

  const resetIdleRatingTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (mode !== "public" || !sessionId || ratingSubmitted || isProfileRequired || quotaBlocked) return;
    idleTimerRef.current = setTimeout(() => {
      setShowRatingPrompt(true);
    }, Math.max(Number(idleRatingWaitSeconds || 120), 5) * 1000);
  };

  const submitSessionRating = async (rating) => {
    if (!sessionId || ratingSubmitted) return;
    setIsSubmittingRating(true);
    try {
      await client.post(`${apiUrl}/api/public/session-rating`, {
        session_id: sessionId,
        rating,
      }, {
        headers: {
          "X-Widget-Key": widgetKey,
          "X-Visitor-Id": visitorId,
        },
      });
      setSelectedRating(rating);
      setRatingSubmitted(true);
      setShowRatingPrompt(false);
      const suffix = widgetKey || "default";
      localStorage.setItem(`chat_session_rating_submitted_${suffix}_${sessionId}`, "true");
    } catch (err) {
      if (err?.response?.status === 409) {
        setRatingSubmitted(true);
        setShowRatingPrompt(false);
        return;
      }
      setPublicConfigError(err?.response?.data?.detail || "Could not submit rating.");
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const getRecaptchaToken = async () => {
    if (!recaptchaSiteKey || !window.grecaptcha?.execute) return null;
    try {
      await new Promise((resolve) => window.grecaptcha.ready(resolve));
      return await window.grecaptcha.execute(recaptchaSiteKey, { action: "visitor_profile" });
    } catch (e) {
      return null; // token failure is handled server-side (rejected if required)
    }
  };

  const submitVisitorProfile = async () => {
    if (!visitorName.trim() || !visitorEmail.trim()) return;
    setIsSavingProfile(true);
    try {
      const recaptchaToken = await getRecaptchaToken();
      const { data } = await client.post(`${apiUrl}/api/public/visitor-profile`, {
        visitor_id: visitorId,
        name: visitorName.trim(),
        email: visitorEmail.trim(),
        recaptcha_token: recaptchaToken,
      }, {
        headers: {
          "X-Widget-Key": widgetKey,
        },
      });
      const resolvedVisitorId = data?.visitor_id;
      if (resolvedVisitorId && resolvedVisitorId !== visitorId) {
        const keySuffix = widgetKey || "default";
        const visitorKey = `chat_visitor_id_${keySuffix}`;
        localStorage.setItem(visitorKey, resolvedVisitorId);
        setVisitorId(resolvedVisitorId);
      }
      setIsProfileRequired(false);
    } catch (err) {
      console.error("Error saving visitor profile:", err);
      const detail = err?.response?.data?.detail;
      if (detail === "captcha_failed") {
        setPublicConfigError("Could not verify you're human. Please try again.");
      } else {
        setPublicConfigError(detail || "Could not save profile. Please try again.");
      }
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Record a click (or downstream conversion) on a product surfaced by the chat assistant,
  // so it shows up in the admin Search Analytics (Result Clicks / Most clicked products).
  const trackProductClick = (product, position, query, eventType = 'click') => {
    if (mode !== 'public' || !widgetKey || !product) return;
    const body = {
      query: query || '',
      product_id: String(product.id || product.product_id || product.url || ''),
      product_name: product.title || '',
      product_url: product.url || '',
      position: typeof position === 'number' ? position + 1 : null,
      event_type: eventType,
    };
    try {
      client.post(`${apiUrl}/api/public/search/click`, body, {
        headers: { 'X-Widget-Key': widgetKey, 'X-Visitor-Id': visitorId },
      }).catch(() => {});
    } catch (e) { /* never block the click */ }
  };

  const handleSubmit = async (e, overrideText) => {
    e?.preventDefault?.();
    const text = (overrideText != null ? String(overrideText) : input).trim();
    if (!text) return;
    if (mode === "public" && !widgetKey) {
      setPublicConfigError("Widget is misconfigured: tenantPublicKey is required.");
      return;
    }
    if (mode === "public" && quotaBlocked) {
      setPublicConfigError(quotaMessage || "Monthly message limit reached.");
      return;
    }
    if (mode === "public" && isProfileRequired) {
      setPublicConfigError("Please provide name and email first.");
      return;
    }

    // Add user message
    const userMessage = {
      type: 'user',
      text: text,
      timestamp: new Date(),
    };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInput('');
    setIsLoading(true);
    setLoadingStage('Understanding your request…');
    setShowRatingPrompt(false);
    resetIdleRatingTimer();

    try {
      const endpoint = mode === "public" ? "/api/public/chat" : "/api/chat";
      const requestUrl = mode === "public" ? `${apiUrl}${endpoint}` : endpoint;
      const chatPayload = {
        message: text,
        session_id: sessionId,
      };
      if (maxResultsDefault != null && maxResultsDefault > 0) {
        chatPayload.max_results = maxResultsDefault;
      }
      const response = await client.post(requestUrl, chatPayload, {
        headers: mode === "public" ? { "X-Widget-Key": widgetKey, "X-Visitor-Id": visitorId } : undefined,
      });
      if (response.data.session_id) {
        const previousSessionId = sessionId;
        setSessionId(response.data.session_id);
        localStorage.setItem(sessionStorageKey, response.data.session_id);
        if (previousSessionId !== response.data.session_id) {
          setRatingSubmitted(false);
          setSelectedRating(0);
        }
      }

      if (response.data.meta?.currency) {
        setCurrencyCode(normalizeCurrencyCode(response.data.meta.currency));
      }

      // Add bot response with the new response format
      const botMessage = {
        type: 'bot',
        text: response.data.response,
        timestamp: new Date(),
        query: text, // the user query that produced these products (for click attribution)
        sources: response.data.sources || [],
        products: response.data.products || [],
        categories: response.data.categories || [],
        actions: response.data.actions || [],
        confidence: response.data.confidence,
        source: response.data.source,
        matchMode: response.data.match_mode || null,
        responseSubtype: response.data.response_subtype || null,
        messageId: response.data.message_id,
        retrievalDebug: response.data.retrieval_debug || null,
      };
      setMessages((prevMessages) => [...prevMessages, botMessage]);
      if (mode === "public") {
        setQuotaBlocked(false);
      }
    } catch (err) {
      console.error('Error querying API:', err);
      if (mode === "public" && err?.response?.status === 429 && err?.response?.data?.detail === "tenant_message_quota_exceeded") {
        const limitMessage = err?.response?.headers?.["x-quota-message"] || "Monthly message limit reached. Please try again next month.";
        setQuotaBlocked(true);
        setQuotaMessage(limitMessage);
        setPublicConfigError(limitMessage);
        return;
      }
      // AI master switch is off for this tenant — show a clear, distinct message
      // instead of the generic "trouble getting your answer" error.
      const detail = err?.response?.data?.detail;
      const aiDisabled = err?.response?.status === 403 && typeof detail === "string" && /ai is disabled/i.test(detail);
      if (aiDisabled) {
        const disabledMessage = {
          type: 'bot',
          text: 'The chat assistant is currently turned off. Please check back later.',
          timestamp: new Date(),
        };
        setMessages((prevMessages) => [...prevMessages, disabledMessage]);
        return;
      }
      // Add error message
      const errorMessage = {
        type: 'bot',
        text: 'Sorry, I had trouble getting your answer. Please try again later.',
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
      setLoadingStage('');
    }
  };

  // Auto-send a query forwarded from the host page (storefront search) once the widget is ready.
  // Waits out the visitor-profile gate / quota / in-flight request; sends exactly once.
  useEffect(() => {
    if (mode !== "public" || !pendingQuery) return;
    if (!widgetKey || !visitorId || !apiUrl) return;
    if (isProfileRequired || quotaBlocked || isLoading) return;
    const q = pendingQuery;
    setPendingQuery(null);
    handleSubmit(null, q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingQuery, widgetKey, visitorId, apiUrl, isProfileRequired, quotaBlocked, isLoading, mode]);

  return (
    <WidgetContainer
      className="mrnwebdesigns-chatbot-widget-container"
      $userBubbleColor={userMessageColor}
      $botBubbleColor={botMessageColor}
    >
      <WidgetHeader className="mrnwebdesigns-chatbot-widget-header" $primaryColor={primaryColor}>
        <HeaderRow>
          {avatarDisplayUrl ? <HeaderAvatar src={avatarDisplayUrl} alt="brand avatar" /> : null}
          <WidgetTitle>{headerTitle}</WidgetTitle>
        </HeaderRow>
      </WidgetHeader>
      {poweredByText && poweredByText.trim() ? <PoweredBy>{poweredByText}</PoweredBy> : null}
      
      <MessageContainer className="mrnwebdesigns-chatbot-widget-message-container">
        {messages.map((message, index) => (
          <React.Fragment key={index}>
            <Message
              type={message.type}
              text={message.text}
              timestamp={message.timestamp}
              sources={message.sources}
              products={message.products}
              query={message.query}
              onProductClick={trackProductClick}
              categories={message.categories}
              actions={message.actions}
              matchMode={message.matchMode}
              isError={message.isError}
              confidence={message.confidence}
              source={message.source}
              messageId={message.messageId}
              mode={mode}
              apiUrl={apiUrl}
              widgetKey={widgetKey}
              userBubbleTextColor={userMessageTextColor}
              botBubbleTextColor={botMessageTextColor}
              currencyCode={currencyCode}
            />
            {message.type === 'bot' && (
              <DebugOverlay trace={message.retrievalDebug} enabled={debugEnabled} />
            )}
          </React.Fragment>
        ))}
        {isLoading && (
          <LoadingMessage>
            {loadingStage ? <LoadingStageText>{loadingStage}</LoadingStageText> : null}
            <LoadingDots>
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </LoadingDots>
          </LoadingMessage>
        )}
        <div ref={messageEndRef} />
      </MessageContainer>

      <InputForm onSubmit={handleSubmit}>
        <Input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your message..."
          disabled={isLoading || (mode === "public" && (isProfileRequired || quotaBlocked))}
        />
        <SendButton
          type="submit"
          disabled={isLoading || !input.trim() || (mode === "public" && (isProfileRequired || quotaBlocked))}
          $primaryColor={primaryColor}
        >
          <BsSend />
        </SendButton>
      </InputForm>
      {mode === "public" && isProfileRequired && (
        <ProfileCaptureContainer>
          <ProfileTitle>Before we continue, please share your details:</ProfileTitle>
          <Input
            type="text"
            value={visitorName}
            onChange={(e) => setVisitorName(e.target.value)}
            placeholder="Your name"
            disabled={isSavingProfile}
          />
          <Input
            type="email"
            value={visitorEmail}
            onChange={(e) => setVisitorEmail(e.target.value)}
            placeholder="Your email"
            disabled={isSavingProfile}
          />
          <ProfileSaveButton
            type="button"
            disabled={isSavingProfile || !visitorName.trim() || !visitorEmail.trim()}
            onClick={submitVisitorProfile}
          >
            {isSavingProfile ? "Saving..." : "Continue to chat"}
          </ProfileSaveButton>
          {recaptchaSiteKey ? (
            <RecaptchaNote>
              This site is protected by reCAPTCHA and the Google{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{" "}
              and{" "}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{" "}
              apply.
            </RecaptchaNote>
          ) : null}
        </ProfileCaptureContainer>
      )}
      {mode === "public" && showRatingPrompt && !ratingSubmitted && sessionId && (
        <RatingContainer>
          <ProfileTitle>Please rate your overall experience:</ProfileTitle>
          <StarRow>
            {[1, 2, 3, 4, 5].map((star) => (
              <StarButton
                key={star}
                type="button"
                disabled={isSubmittingRating}
                onClick={() => submitSessionRating(star)}
              >
                {star}
              </StarButton>
            ))}
          </StarRow>
        </RatingContainer>
      )}
      {mode === "public" && ratingSubmitted && selectedRating > 0 && (
        <RatingSubmittedText>Thanks for rating: {selectedRating}/5</RatingSubmittedText>
      )}
      {(privacyPolicyUrl || widgetWebsiteUrl) && (
        <FooterLinks>
          {widgetWebsiteUrl ? (
            <PrivacyPolicyLink href={widgetWebsiteUrl} target="_blank" rel="noreferrer">
              Website
            </PrivacyPolicyLink>
          ) : null}
          {privacyPolicyUrl && widgetWebsiteUrl ? <FooterSep aria-hidden>|</FooterSep> : null}
          {privacyPolicyUrl ? (
            <PrivacyPolicyLink href={privacyPolicyUrl} target="_blank" rel="noreferrer">
              Privacy Policy
            </PrivacyPolicyLink>
          ) : null}
        </FooterLinks>
      )}
      {publicConfigError && <ConfigError>{publicConfigError}</ConfigError>}
    </WidgetContainer>
  );
};

// Styled components
const WidgetContainer = styled.div`
  --primary-color: ${(p) => p.$userBubbleColor || "#bf362e"};
  --secondary-color: ${(p) => p.$botBubbleColor || "#d5bbb9"};
  display: flex;
  flex-direction: column;
  width: 380px;
  height: 600px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const WidgetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: ${(props) => props.$primaryColor || "#bf362e"};
  color: white;
`;

const WidgetTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderAvatar = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 999px;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.2);
`;

const PoweredBy = styled.div`
  font-size: 0.75rem;
  color: #666;
  padding: 6px 15px 0;
`;

const MessageContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InputForm = styled.form`
  display: flex;
  padding: 15px;
  gap: 10px;
  border-top: 1px solid #eee;
`;

const Input = styled.input`
  flex: 1;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 20px;
  outline: none;
  
  &:focus {
    border-color: ${(props) => props.$primaryColor || "#bf362e"};
  }
`;

const SendButton = styled.button`
  background: ${(props) => props.$primaryColor || "#bf362e"};
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    filter: brightness(0.92);
  }
`;

const LoadingMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  color: #666;
  gap: 6px;
`;

const LoadingStageText = styled.div`
  font-size: 12px;
  opacity: 0.85;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 2px;
  
  span {
    animation: loading 1.4s infinite;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
  
  @keyframes loading {
    0%, 80%, 100% { opacity: 0; }
    40% { opacity: 1; }
  }
`;

const ConfigError = styled.div`
  color: #c62828;
  font-size: 0.85rem;
  padding: 8px 12px 12px;
`;

const FooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 0 15px 8px;
  font-size: 0.75rem;
`;

const FooterSep = styled.span`
  color: #999;
  user-select: none;
`;

const PrivacyPolicyLink = styled.a`
  color: #555;
  text-decoration: underline;
`;

const ProfileCaptureContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid #eee;
  padding: 12px 15px;
`;

const ProfileTitle = styled.div`
  font-size: 0.85rem;
  color: #444;
`;

const RecaptchaNote = styled.div`
  font-size: 0.65rem;
  color: #888;
  line-height: 1.3;
  margin-top: 6px;
  a {
    color: #888;
    text-decoration: underline;
  }
`;

const ProfileSaveButton = styled.button`
  border: none;
  background: #bf362e;
  color: white;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const RatingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid #eee;
  padding: 12px 15px;
`;

const StarRow = styled.div`
  display: flex;
  gap: 6px;
`;

const StarButton = styled.button`
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
`;

const RatingSubmittedText = styled.div`
  color: #2e7d32;
  font-size: 0.8rem;
  padding: 0 15px 8px;
`;

export default ChatWidget; 